import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { NAV_GROUPS, ROUTES } from './routes'
import { useAuth } from '../auth/AuthContext'
import { useTheme } from '../theme/ThemeProvider'
import { formatRole } from '../lib/format'
import { replayTour, roleHasTour } from '../tour/tours'
import Icon from '../ui/icons'

/* Phone-only full-screen menu (mobile.css ≤640), opened by the header's
   menu button in place of the desktop sidebar drawer. Same routes, same
   permission filter and same badges as Sidebar.jsx — only laid out as an
   app menu: profile card, a tile grid per nav group, then settings. */

const TONES = ['var(--primary)', 'var(--info)', 'var(--warning)', 'var(--purple)', 'var(--danger)', 'var(--success)']
const ALERT_BADGES = new Set(['inbox', 'notifications'])

function initials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

export default function MobileMenu({ open, onClose, badges }) {
  const { user, can, signOut, preview } = useAuth()
  const { theme, toggleTheme } = useTheme()
  const [term, setTerm] = useState('')
  const [searching, setSearching] = useState(false)
  const searchRef = useRef(null)
  const bodyRef = useRef(null)

  /* Each opening starts clean: no leftover filter, scrolled to the top. */
  useEffect(() => {
    if (!open) return
    setTerm('')
    setSearching(false)
    if (bodyRef.current) bodyRef.current.scrollTop = 0
  }, [open])

  const q = term.trim().toLowerCase()
  const visible = ROUTES.filter((r) => !r.hidden && can(r.permission) && (!q || r.title.toLowerCase().includes(q)))
  const name = user?.name || 'Guest'
  const role = formatRole(user?.role_name || user?.role) || 'Member'

  let tone = 0
  return (
    <div className={`mmenu${open ? ' open' : ''}`} role="dialog" aria-modal="true" aria-label="Menu" inert={!open}>
      <header className="mmenu-head">
        {searching ? (
          <input
            ref={searchRef}
            type="search"
            className="mmenu-search"
            placeholder="Search menu…"
            aria-label="Search menu"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
          />
        ) : (
          <h1>Menu</h1>
        )}
        <button
          type="button"
          className="mmenu-icon-btn"
          aria-label={searching ? 'Close search' : 'Search menu'}
          onClick={() => {
            if (searching) { setSearching(false); setTerm(''); return }
            setSearching(true)
            requestAnimationFrame(() => searchRef.current?.focus())
          }}
        >
          <Icon name={searching ? 'x' : 'search'} />
        </button>
        {!searching && (
          <button type="button" className="mmenu-icon-btn" aria-label="Close menu" onClick={onClose}>
            <Icon name="x" />
          </button>
        )}
      </header>

      <div className="mmenu-body" ref={bodyRef}>
        {!q && (
          <Link to="/profile" className="mmenu-profile" onClick={onClose}>
            <span className={`mmenu-avatar${user?.avatar_url ? ' has-photo' : ''}`}>
              {user?.avatar_url ? <img src={user.avatar_url} alt="" /> : initials(name)}
            </span>
            <span className="mmenu-profile-text">
              <strong>{name}</strong>
              <span>{role} · Utopia Brands</span>
            </span>
            <Icon name="chevron-right" />
          </Link>
        )}

        {/* Right under the profile, so theme and log out are seen on opening
            rather than found at the bottom. Settings and Help are tiles under System. */}
        {!q && (
          <section className="mmenu-settings">
            <button type="button" className="mmenu-row" role="switch" aria-checked={theme === 'dark'} onClick={toggleTheme}>
              <span className="mmenu-row-icon"><Icon name={theme === 'dark' ? 'moon' : 'sun'} /></span>
              <span className="mmenu-row-label">
                Dark mode
                <small>{theme === 'dark' ? 'On — tap for light mode' : 'Off — tap for dark mode'}</small>
              </span>
              <span className="mmenu-switch" aria-hidden="true"><i /></span>
            </button>
            {/* The guided tour of their role, again (tour/tours.js); only roles that have one. */}
            {!preview && roleHasTour(user?.role_name) && (
              <button type="button" className="mmenu-row" onClick={() => { onClose(); replayTour() }}>
                <span className="mmenu-row-icon"><Icon name="sparkles" /></span>
                <span className="mmenu-row-label">
                  Take the tour
                  <small>A quick walk around your pages</small>
                </span>
              </button>
            )}
            <button type="button" className="mmenu-row is-danger" onClick={signOut}>
              <span className="mmenu-row-icon"><Icon name="logout" /></span>
              <span className="mmenu-row-label">Log out</span>
            </button>
          </section>
        )}

        {NAV_GROUPS.map((group) => {
          const items = visible.filter((r) => r.group === group)
          if (!items.length) return null
          return (
            <section className="mmenu-group" key={group}>
              <h2>{group}</h2>
              <div className="mmenu-grid">
                {items.map((r) => {
                  const k = TONES[tone++ % TONES.length]
                  const count = r.badge ? badges?.[r.badge] : null
                  return (
                    <Link
                      key={r.path}
                      to={`/${r.path}`}
                      className={`mmenu-tile${r.comingSoon ? ' is-soon' : ''}`}
                      style={{ '--k': k }}
                      onClick={onClose}
                    >
                      <span className="mmenu-tile-icon"><Icon name={r.icon} /></span>
                      <span className="mmenu-tile-label">{r.title}</span>
                      {r.comingSoon ? (
                        <span className="mmenu-badge is-soon">Soon</span>
                      ) : count ? (
                        <span className={`mmenu-badge${ALERT_BADGES.has(r.badge) ? ' is-alert' : ''}`}>{count}</span>
                      ) : null}
                    </Link>
                  )
                })}
              </div>
            </section>
          )
        })}

        {q && !visible.length && <p className="mmenu-empty">Nothing in the menu matches “{term.trim()}”.</p>}
      </div>
    </div>
  )
}
